import React, { useEffect, useState } from 'react';
import PageLayout from '../../components/layout/PageLayout';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { settingsStore } from '../../lib/localSettingsStore';
import { supabaseHelpers } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const SettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const { user } = useAuth?.() || {};
  const [suppliers, setSuppliers] = useState([]);
  const [newSupplier, setNewSupplier] = useState('');
  const [editingSupplier, setEditingSupplier] = useState(null); // oldName
  const [editingSupplierValue, setEditingSupplierValue] = useState('');

  const [materials, setMaterials] = useState([]);
  const [newMaterialName, setNewMaterialName] = useState('');
  const [newMaterialDensity, setNewMaterialDensity] = useState('');
  const [editingMaterial, setEditingMaterial] = useState(null); // oldName
  const [editingMaterialName, setEditingMaterialName] = useState('');
  const [editingMaterialDensity, setEditingMaterialDensity] = useState('');

  // Filament brands
  const [filamentBrands, setFilamentBrands] = useState([]);
  const [newFilamentBrand, setNewFilamentBrand] = useState('');
  const [editingFilamentBrand, setEditingFilamentBrand] = useState(null);
  const [editingFilamentBrandValue, setEditingFilamentBrandValue] = useState('');

  // Printer brands
  const [printerBrands, setPrinterBrands] = useState([]);
  const [newPrinterBrand, setNewPrinterBrand] = useState('');
  const [editingPrinterBrand, setEditingPrinterBrand] = useState(null);
  const [editingPrinterBrandValue, setEditingPrinterBrandValue] = useState('');

  // Printer models by brand
  const [selectedBrandForModels, setSelectedBrandForModels] = useState('');
  const [brandModels, setBrandModels] = useState([]);
  const [newModelName, setNewModelName] = useState('');
  const [editingModelName, setEditingModelName] = useState(null);
  const [editingModelNameValue, setEditingModelNameValue] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [sRes, mRes, fbRes, pbRes] = await Promise.all([
          settingsStore.getSuppliers(),
          settingsStore.getMaterialTypes(),
          settingsStore.getFilamentBrands(),
          settingsStore.getPrinterBrands()
        ]);
        setSuppliers(Array.isArray(sRes?.data) ? sRes.data : []);
        setMaterials(Array.isArray(mRes?.data) ? mRes.data : []);
        setFilamentBrands(Array.isArray(fbRes?.data) ? fbRes.data : []);
        setPrinterBrands(Array.isArray(pbRes?.data) ? pbRes.data : []);
      } catch (err) {
        console.error('Erro ao carregar configurações:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Atualiza modelos quando muda a marca selecionada
  useEffect(() => {
    let active = true;
    const loadModels = async () => {
      if (!selectedBrandForModels) { setBrandModels([]); return; }
      const { data } = await settingsStore.getPrinterModelsByBrand(selectedBrandForModels);
      if (active) setBrandModels(Array.isArray(data) ? data : []);
    };
    loadModels();
    return () => { active = false; };
  }, [selectedBrandForModels]);

  const handleAddSupplier = async () => {
    const name = String(newSupplier || '').trim();
    if (!name) return;
    const { error } = await settingsStore.createSupplier(name);
    if (!error) {
      const { data } = await settingsStore.getSuppliers();
      setSuppliers(Array.isArray(data) ? data : []);
      setNewSupplier('');
    } else {
      alert('Erro ao adicionar fornecedor');
    }
  };

  const startEditSupplier = (name) => {
    setEditingSupplier(name);
    setEditingSupplierValue(name);
  };

  const saveEditSupplier = async () => {
    const oldName = editingSupplier;
    const newName = String(editingSupplierValue || '').trim();
    if (!oldName || !newName) return;
    const { error } = await settingsStore.updateSupplier(oldName, newName);
    if (!error) {
      const { data } = await settingsStore.getSuppliers();
      setSuppliers(Array.isArray(data) ? data : []);
      setEditingSupplier(null);
      setEditingSupplierValue('');
    } else {
      alert('Erro ao editar fornecedor');
    }
  };

  const deleteSupplier = async (name) => {
    if (!confirm(`Excluir fornecedor "${name}"?`)) return;
    const { error } = await settingsStore.deleteSupplier(name);
    if (!error) {
      const { data } = await settingsStore.getSuppliers();
      setSuppliers(Array.isArray(data) ? data : []);
    } else {
      alert('Erro ao excluir fornecedor');
    }
  };

  const handleAddMaterial = async () => {
    const name = String(newMaterialName || '').trim();
    const density = parseFloat(newMaterialDensity);
    if (!name) return;
    const { error } = await settingsStore.createMaterialType({ name, density });
    if (!error) {
      const { data } = await settingsStore.getMaterialTypes();
      setMaterials(Array.isArray(data) ? data : []);
      setNewMaterialName('');
      setNewMaterialDensity('');
    } else {
      alert('Erro ao adicionar material');
    }
  };

  const startEditMaterial = (mt) => {
    setEditingMaterial(mt?.name);
    setEditingMaterialName(mt?.name);
    setEditingMaterialDensity(String(mt?.density ?? ''));
  };

  const saveEditMaterial = async () => {
    const oldName = editingMaterial;
    const name = String(editingMaterialName || '').trim();
    const density = parseFloat(editingMaterialDensity);
    if (!oldName || !name) return;
    const { error } = await settingsStore.updateMaterialType(oldName, { name, density });
    if (!error) {
      const { data } = await settingsStore.getMaterialTypes();
      setMaterials(Array.isArray(data) ? data : []);
      setEditingMaterial(null);
      setEditingMaterialName('');
      setEditingMaterialDensity('');
    } else {
      alert('Erro ao editar material');
    }
  };

  const deleteMaterial = async (name) => {
    if (!confirm(`Excluir material "${name}"?`)) return;
    const { error } = await settingsStore.deleteMaterialType(name);
    if (!error) {
      const { data } = await settingsStore.getMaterialTypes();
      setMaterials(Array.isArray(data) ? data : []);
    } else {
      alert('Erro ao excluir material');
    }
  };

  const resetMaterialsToDefault = async () => {
    if (!confirm('Restaurar lista de materiais ao padrão?')) return;
    const { error } = await settingsStore.resetMaterialTypesToDefault();
    if (!error) {
      const { data } = await settingsStore.getMaterialTypes();
      setMaterials(Array.isArray(data) ? data : []);
    }
  };

  // CRUD Filament Brands
  const handleAddFilamentBrand = async () => {
    const name = String(newFilamentBrand || '').trim();
    if (!name) return;
    const { error } = await settingsStore.createFilamentBrand(name);
    if (!error) {
      const { data } = await settingsStore.getFilamentBrands();
      setFilamentBrands(Array.isArray(data) ? data : []);
      setNewFilamentBrand('');
    } else { alert('Erro ao adicionar marca de filamento'); }
  };
  const startEditFilamentBrand = (name) => { setEditingFilamentBrand(name); setEditingFilamentBrandValue(name); };
  const saveEditFilamentBrand = async () => {
    const oldName = editingFilamentBrand;
    const newName = String(editingFilamentBrandValue || '').trim();
    if (!oldName || !newName) return;
    const { error } = await settingsStore.updateFilamentBrand(oldName, newName);
    if (!error) {
      const { data } = await settingsStore.getFilamentBrands();
      setFilamentBrands(Array.isArray(data) ? data : []);
      setEditingFilamentBrand(null); setEditingFilamentBrandValue('');
    } else { alert('Erro ao editar marca de filamento'); }
  };
  const deleteFilamentBrand = async (name) => {
    if (!confirm(`Excluir marca "${name}"?`)) return;
    const { error } = await settingsStore.deleteFilamentBrand(name);
    if (!error) { const { data } = await settingsStore.getFilamentBrands(); setFilamentBrands(Array.isArray(data) ? data : []); }
  };

  // CRUD Printer Brands
  const handleAddPrinterBrand = async () => {
    const name = String(newPrinterBrand || '').trim();
    if (!name) return;
    const { error } = await settingsStore.createPrinterBrand(name);
    if (!error) {
      const { data } = await settingsStore.getPrinterBrands();
      setPrinterBrands(Array.isArray(data) ? data : []);
      setNewPrinterBrand('');
    } else { alert('Erro ao adicionar marca de impressora'); }
  };
  const startEditPrinterBrand = (name) => { setEditingPrinterBrand(name); setEditingPrinterBrandValue(name); };
  const saveEditPrinterBrand = async () => {
    const oldName = editingPrinterBrand;
    const newName = String(editingPrinterBrandValue || '').trim();
    if (!oldName || !newName) return;
    const { error } = await settingsStore.updatePrinterBrand(oldName, newName);
    if (!error) {
      const { data } = await settingsStore.getPrinterBrands();
      setPrinterBrands(Array.isArray(data) ? data : []);
      setEditingPrinterBrand(null); setEditingPrinterBrandValue('');
    } else { alert('Erro ao editar marca de impressora'); }
  };
  const deletePrinterBrand = async (name) => {
    if (!confirm(`Excluir marca "${name}"?`)) return;
    const { error } = await settingsStore.deletePrinterBrand(name);
    if (!error) { const { data } = await settingsStore.getPrinterBrands(); setPrinterBrands(Array.isArray(data) ? data : []); }
  };

  // CRUD Models by Brand
  const handleAddModelForBrand = async () => {
    const brand = String(selectedBrandForModels || '').trim();
    const model = String(newModelName || '').trim();
    if (!brand || !model) return;
    const { error } = await settingsStore.createPrinterModelForBrand(brand, model);
    if (!error) {
      const { data } = await settingsStore.getPrinterModelsByBrand(brand);
      setBrandModels(Array.isArray(data) ? data : []);
      setNewModelName('');
    } else { alert('Erro ao adicionar modelo'); }
  };
  const startEditModel = (name) => { setEditingModelName(name); setEditingModelNameValue(name); };
  const saveEditModel = async () => {
    const brand = String(selectedBrandForModels || '').trim();
    const oldName = editingModelName;
    const newName = String(editingModelNameValue || '').trim();
    if (!brand || !oldName || !newName) return;
    const { error } = await settingsStore.updatePrinterModelForBrand(brand, oldName, newName);
    if (!error) {
      const { data } = await settingsStore.getPrinterModelsByBrand(brand);
      setBrandModels(Array.isArray(data) ? data : []);
      setEditingModelName(null); setEditingModelNameValue('');
    } else { alert('Erro ao editar modelo'); }
  };
  const deleteModel = async (name) => {
    const brand = String(selectedBrandForModels || '').trim();
    if (!confirm(`Excluir modelo "${name}" da marca "${brand}"?`)) return;
    const { error } = await settingsStore.deletePrinterModelForBrand(brand, name);
    if (!error) { const { data } = await settingsStore.getPrinterModelsByBrand(brand); setBrandModels(Array.isArray(data) ? data : []); }
  };

  // Mesclar com inventário (filamentos + impressoras)
  const mergeWithInventory = async () => {
    try {
      const uid = user?.id || 'guest';
      const [printersRes, filamentsRes] = await Promise.all([
        supabaseHelpers.getUserPrinters(uid),
        supabaseHelpers.getUserFilaments(uid)
      ]);
      const printers = Array.isArray(printersRes?.data) ? printersRes.data : [];
      const filaments = Array.isArray(filamentsRes?.data) ? filamentsRes.data : [];
      const suppliersSet = new Set([
        ...filaments.map(f => String(f?.supplier || '').trim()),
        ...printers.map(p => String(p?.supplier || '').trim())
      ].filter(Boolean));
      const filamentBrandSet = new Set(filaments.map(f => String(f?.brand || '').trim()).filter(Boolean));
      const printerBrandSet = new Set(printers.map(p => String(p?.brand || p?.manufacturer || '').trim()).filter(Boolean));
      const modelsMap = {};
      printers.forEach(p => {
        const brand = String(p?.brand || p?.manufacturer || '').trim();
        const model = String(p?.model || '').trim();
        if (!brand || !model) return;
        const key = brand.toLowerCase();
        modelsMap[key] = modelsMap[key] || [];
        modelsMap[key].push(model);
      });
      await settingsStore.mergeSuppliers(Array.from(suppliersSet));
      await settingsStore.mergeFilamentBrands(Array.from(filamentBrandSet));
      await settingsStore.mergePrinterBrands(Array.from(printerBrandSet));
      await settingsStore.mergePrinterModelsByBrand(modelsMap);
      // Reload lists
      const [sRes, fbRes, pbRes] = await Promise.all([
        settingsStore.getSuppliers(),
        settingsStore.getFilamentBrands(),
        settingsStore.getPrinterBrands()
      ]);
      setSuppliers(Array.isArray(sRes?.data) ? sRes.data : []);
      setFilamentBrands(Array.isArray(fbRes?.data) ? fbRes.data : []);
      setPrinterBrands(Array.isArray(pbRes?.data) ? pbRes.data : []);
      if (selectedBrandForModels) {
        const { data } = await settingsStore.getPrinterModelsByBrand(selectedBrandForModels);
        setBrandModels(Array.isArray(data) ? data : []);
      }
      alert('Mesclagem concluída com sucesso!');
    } catch (err) {
      console.error('Erro ao mesclar com inventário:', err);
      alert('Erro ao mesclar com inventário');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <span className="text-lg text-foreground">Carregando configurações...</span>
        </div>
      </div>
    );
  }

  return (
    <PageLayout
      title="Configurações"
      subtitle="Gerencie fornecedores, marcas e materiais; mescle com inventário."
      iconName="Settings"
    >
      <div className="flex items-center justify-end mb-4">
        <Button variant="outline" onClick={mergeWithInventory} iconName="Merge" iconPosition="left">Mesclar com inventário</Button>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Suppliers */}
        <section className="bg-card/80 backdrop-blur-lg border border-border rounded-2xl p-6 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-white"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">Fornecedores</h2>
                <p className="text-sm text-muted-foreground">Cadastre, edite e exclua fornecedores</p>
              </div>
            </div>
          </div>
          <div className="flex items-end space-x-2 mb-4">
            <div className="flex-1"><Input label="Novo fornecedor" placeholder="Ex.: Amazon" value={newSupplier} onChange={(e)=>setNewSupplier(e?.target?.value)} /></div>
            <Button variant="primary" onClick={handleAddSupplier}>Adicionar</Button>
          </div>
          <div className="divide-y divide-border rounded-lg overflow-hidden border border-border">
            {suppliers?.length === 0 && (
              <div className="p-4 text-sm text-muted-foreground">Nenhum fornecedor cadastrado.</div>
            )}
            {suppliers?.map((name) => {
              const isEditing = editingSupplier === name;
              return (
                <div key={name} className="flex items-center justify-between p-4 bg-surface">
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <Input value={editingSupplierValue} onChange={(e)=>setEditingSupplierValue(e?.target?.value)} />
                    ) : (
                      <div className="text-sm font-medium text-foreground truncate" title={name}>{name}</div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    {isEditing ? (
                      <>
                        <Button size="sm" variant="success" onClick={saveEditSupplier}>Salvar</Button>
                        <Button size="sm" variant="ghost" onClick={()=>{setEditingSupplier(null); setEditingSupplierValue('');}}>Cancelar</Button>
                      </>
                    ) : (
                      <>
                        <Button size="sm" variant="ghost" onClick={()=>startEditSupplier(name)}>Editar</Button>
                        <Button size="sm" variant="error" onClick={()=>deleteSupplier(name)}>Excluir</Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Materials */}
        <section className="bg-card/80 backdrop-blur-lg border border-border rounded-2xl p-6 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-white"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">Tipos de Materiais</h2>
                <p className="text-sm text-muted-foreground">Gerencie nome e densidade (g/cm³)</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
            <Input label="Nome" placeholder="Ex.: PLA" value={newMaterialName} onChange={(e)=>setNewMaterialName(e?.target?.value)} />
            <Input label="Densidade" placeholder="Ex.: 1.24" value={newMaterialDensity} onChange={(e)=>setNewMaterialDensity(e?.target?.value)} />
            <div className="flex items-end"><Button className="w-full" variant="primary" onClick={handleAddMaterial}>Adicionar</Button></div>
          </div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-muted-foreground">Edite ou exclua materiais existentes. Densidade em g/cm³.</p>
            <Button size="sm" variant="warning" onClick={resetMaterialsToDefault}>Voltar ao padrão</Button>
          </div>
          <div className="divide-y divide-border rounded-lg overflow-hidden border border-border">
            {materials?.length === 0 && (
              <div className="p-4 text-sm text-muted-foreground">Nenhum material cadastrado.</div>
            )}
            {materials?.map((mt) => {
              const isEditing = editingMaterial === mt?.name;
              return (
                <div key={mt?.name} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center p-4 bg-surface">
                  <div className="md:col-span-1">
                    {isEditing ? (
                      <Input value={editingMaterialName} onChange={(e)=>setEditingMaterialName(e?.target?.value)} />
                    ) : (
                      <div className="text-sm font-medium text-foreground truncate" title={mt?.name}>{mt?.name}</div>
                    )}
                  </div>
                  <div className="md:col-span-1">
                    {isEditing ? (
                      <Input value={editingMaterialDensity} onChange={(e)=>setEditingMaterialDensity(e?.target?.value)} />
                    ) : (
                      <div className="text-sm text-muted-foreground">Densidade: <span className="text-foreground font-medium">{Number(mt?.density ?? 0) || ''}</span></div>
                    )}
                  </div>
                  <div className="md:col-span-1 flex items-center justify-end space-x-2">
                    {isEditing ? (
                      <>
                        <Button size="sm" variant="success" onClick={saveEditMaterial}>Salvar</Button>
                        <Button size="sm" variant="ghost" onClick={()=>{setEditingMaterial(null); setEditingMaterialName(''); setEditingMaterialDensity('');}}>Cancelar</Button>
                      </>
                    ) : (
                      <>
                        <Button size="sm" variant="ghost" onClick={()=>startEditMaterial(mt)}>Editar</Button>
                        <Button size="sm" variant="error" onClick={()=>deleteMaterial(mt?.name)}>Excluir</Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Filament Brands */}
        <section className="bg-card/80 backdrop-blur-lg border border-border rounded-2xl p-6 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-white"><path d="M7 6h10M7 12h10M7 18h10"/></svg>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">Marcas de Filamentos</h2>
                <p className="text-sm text-muted-foreground">Cadastre, edite e exclua marcas</p>
              </div>
            </div>
          </div>
          <div className="flex items-end space-x-2 mb-4">
            <div className="flex-1"><Input label="Nova marca" placeholder="Ex.: eSUN" value={newFilamentBrand} onChange={(e)=>setNewFilamentBrand(e?.target?.value)} /></div>
            <Button variant="primary" onClick={handleAddFilamentBrand}>Adicionar</Button>
          </div>
          <div className="divide-y divide-border rounded-lg overflow-hidden border border-border">
            {filamentBrands?.length === 0 && (<div className="p-4 text-sm text-muted-foreground">Nenhuma marca cadastrada.</div>)}
            {filamentBrands?.map((name) => {
              const isEditing = editingFilamentBrand === name;
              return (
                <div key={name} className="flex items-center justify-between p-4 bg-surface">
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <Input value={editingFilamentBrandValue} onChange={(e)=>setEditingFilamentBrandValue(e?.target?.value)} />
                    ) : (
                      <div className="text-sm font-medium text-foreground truncate" title={name}>{name}</div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    {isEditing ? (
                      <>
                        <Button size="sm" variant="success" onClick={saveEditFilamentBrand}>Salvar</Button>
                        <Button size="sm" variant="ghost" onClick={()=>{setEditingFilamentBrand(null); setEditingFilamentBrandValue('');}}>Cancelar</Button>
                      </>
                    ) : (
                      <>
                        <Button size="sm" variant="ghost" onClick={()=>startEditFilamentBrand(name)}>Editar</Button>
                        <Button size="sm" variant="error" onClick={()=>deleteFilamentBrand(name)}>Excluir</Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Printer Brands & Models by Brand */}
        <section className="bg-card/80 backdrop-blur-lg border border-border rounded-2xl p-6 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-white"><path d="M4 6h16v12H4zM8 10h8v4H8z"/></svg>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">Marcas e Modelos de Impressoras</h2>
                <p className="text-sm text-muted-foreground">Gerencie marcas e modelos por marca</p>
              </div>
            </div>
          </div>
          <div className="flex items-end space-x-2 mb-4">
            <div className="flex-1"><Input label="Nova marca de impressora" placeholder="Ex.: Bambu Lab" value={newPrinterBrand} onChange={(e)=>setNewPrinterBrand(e?.target?.value)} /></div>
            <Button variant="primary" onClick={handleAddPrinterBrand}>Adicionar Marca</Button>
          </div>
          <div className="divide-y divide-border rounded-lg overflow-hidden border border-border mb-6">
            {printerBrands?.length === 0 && (<div className="p-4 text-sm text-muted-foreground">Nenhuma marca cadastrada.</div>)}
            {printerBrands?.map((name) => {
              const isEditing = editingPrinterBrand === name;
              return (
                <div key={name} className="flex items-center justify-between p-4 bg-surface">
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <Input value={editingPrinterBrandValue} onChange={(e)=>setEditingPrinterBrandValue(e?.target?.value)} />
                    ) : (
                      <div className="text-sm font-medium text-foreground truncate" title={name}>{name}</div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    {isEditing ? (
                      <>
                        <Button size="sm" variant="success" onClick={saveEditPrinterBrand}>Salvar</Button>
                        <Button size="sm" variant="ghost" onClick={()=>{setEditingPrinterBrand(null); setEditingPrinterBrandValue('');}}>Cancelar</Button>
                      </>
                    ) : (
                      <>
                        <Button size="sm" variant="ghost" onClick={()=>startEditPrinterBrand(name)}>Editar</Button>
                        <Button size="sm" variant="error" onClick={()=>deletePrinterBrand(name)}>Excluir</Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Modelos por marca */}
          <div className="bg-surface border border-border rounded-xl p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
              <div className="md:col-span-1">
                <Input label="Marca" placeholder="Selecione uma marca acima e digite aqui" value={selectedBrandForModels} onChange={(e)=>setSelectedBrandForModels(e?.target?.value)} />
              </div>
              <div className="md:col-span-1">
                <Input label="Novo modelo" placeholder="Ex.: A1 Mini" value={newModelName} onChange={(e)=>setNewModelName(e?.target?.value)} />
              </div>
              <div className="md:col-span-1 flex items-end">
                <Button className="w-full" variant="primary" onClick={handleAddModelForBrand} disabled={!selectedBrandForModels}>Adicionar Modelo</Button>
              </div>
            </div>
            <div className="divide-y divide-border rounded-lg overflow-hidden border border-border">
              {(!brandModels || brandModels.length === 0) && (
                <div className="p-4 text-sm text-muted-foreground">Nenhum modelo para esta marca.</div>
              )}
              {brandModels?.map((name) => {
                const isEditing = editingModelName === name;
                return (
                  <div key={name} className="flex items-center justify-between p-4 bg-muted/20">
                    <div className="flex-1 min-w-0">
                      {isEditing ? (
                        <Input value={editingModelNameValue} onChange={(e)=>setEditingModelNameValue(e?.target?.value)} />
                      ) : (
                        <div className="text-sm font-medium text-foreground truncate" title={name}>{name}</div>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      {isEditing ? (
                        <>
                          <Button size="sm" variant="success" onClick={saveEditModel}>Salvar</Button>
                          <Button size="sm" variant="ghost" onClick={()=>{setEditingModelName(null); setEditingModelNameValue('');}}>Cancelar</Button>
                        </>
                      ) : (
                        <>
                          <Button size="sm" variant="ghost" onClick={()=>startEditModel(name)}>Editar</Button>
                          <Button size="sm" variant="error" onClick={()=>deleteModel(name)}>Excluir</Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </div>
    </PageLayout>
  );
};

export default SettingsPage;